import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Award,
    Plus,
    Search,
    Pencil,
    Trash2,
    Check,
    X,
    Calendar,
    Users,
    Briefcase,
    Shield,
    UsersRound
} from "lucide-react";
import {
    payrollDeleteResourceRequest,
    payrollFetchResourceRequest,
    payrollSaveResourceRequest
} from "../../../redux/actions/payrollActions";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { getRequest } from "../../../../api";
import "./ParamPrimesPage.css";

const API_BASE_URL = "/api";

const emptyPrime = {
    rubriqueId: "",
    modeCalcul: "FIXE",
    montantFixe: "",
    pourcentage: "",
    moisVersement: "",
    actif: true,
    dateDebut: new Date().toISOString().slice(0, 10),
    dateFin: "",
    typesContrat: [],
    tranches: [],
    cibles: []
};

const emptyTranche = {
    minAnnees: "",
    maxAnnees: "",
    taux: ""
};

const ParamPrimesPage = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const primes = payroll.parametragesPrimes || [];
    
    // Core references
    const [rubriques, setRubriques] = useState([]);
    const [emplois, setEmplois] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [users, setUsers] = useState([]);

    // UI States
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [activeModalTab, setActiveModalTab] = useState("identite");
    const [editingPrime, setEditingPrime] = useState(null);
    const [form, setForm] = useState(emptyPrime);

    // Filter states for UI cascades (Targeting population)
    const [selectedEmploiId, setSelectedEmploiId] = useState("");
    const [selectedJobId, setSelectedJobId] = useState("");

    // Load base data
    useEffect(() => {
        dispatch(payrollFetchResourceRequest("parametragesPrimes"));
        
        // Fetch reference lists from APIs
        const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
        const uid = sessionStorage.getItem("uid") || localStorage.getItem("uid") || "";
        const headers = { uid, Authorization: `Bearer ${token}` };

        getRequest({ url: `${API_BASE_URL}/rubriques-paie`, token })
            .then(res => setRubriques((res.data || []).filter(r => r.type === "GAIN")))
            .catch(err => console.error("Error fetching rubriques", err));

        getRequest({ url: `${API_BASE_URL}/emplois`, token })
            .then(res => setEmplois(res.data || []))
            .catch(err => console.error("Error fetching emplois", err));

        getRequest({ url: `${API_BASE_URL}/jobs`, token })
            .then(res => setJobs(res.data || []))
            .catch(err => console.error("Error fetching jobs", err));

        getRequest({ url: `${API_BASE_URL}/users`, token })
            .then(res => setUsers(res.data || []))
            .catch(err => console.error("Error fetching users", err));
    }, [dispatch]);

    // Derived filtering lists based on contract and cascade selection
    const filteredEmplois = useMemo(() => {
        // contract types inside form.typesContrat will filter users, then we can filter jobs and emplois
        if (form.typesContrat.length === 0) return emplois;
        const matchingUsers = users.filter(u => form.typesContrat.includes(u.typeContrat));
        const matchingJobIds = matchingUsers.map(u => u.job?.id).filter(Boolean);
        const matchingJobs = jobs.filter(j => matchingJobIds.includes(j.id));
        const matchingEmploiIds = matchingJobs.map(j => j.emploi?.id).filter(Boolean);
        return emplois.filter(e => matchingEmploiIds.includes(e.id));
    }, [emplois, users, jobs, form.typesContrat]);

    const filteredJobs = useMemo(() => {
        let list = jobs;
        if (form.typesContrat.length > 0) {
            const matchingUsers = users.filter(u => form.typesContrat.includes(u.typeContrat));
            const matchingJobIds = matchingUsers.map(u => u.job?.id).filter(Boolean);
            list = list.filter(j => matchingJobIds.includes(j.id));
        }
        if (selectedEmploiId) {
            list = list.filter(j => j.emploi?.id === Number(selectedEmploiId));
        }
        return list;
    }, [jobs, users, form.typesContrat, selectedEmploiId]);

    const filteredUsers = useMemo(() => {
        let list = users;
        if (form.typesContrat.length > 0) {
            list = list.filter(u => form.typesContrat.includes(u.typeContrat));
        }
        if (selectedJobId) {
            list = list.filter(u => u.job?.id === Number(selectedJobId));
        } else if (selectedEmploiId) {
            const matchingJobIds = jobs.filter(j => j.emploi?.id === Number(selectedEmploiId)).map(j => j.id);
            list = list.filter(u => u.job && matchingJobIds.includes(u.job.id));
        }
        return list;
    }, [users, jobs, form.typesContrat, selectedEmploiId, selectedJobId]);

    // Helpers
    const formatMoney = (val) => val ? Number(val).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " DH" : "—";
    const formatPercent = (val) => val ? Number(val).toFixed(2) + " %" : "—";

    // Edit/Add Mode logic
    const handleOpenModal = (prime = null) => {
        if (prime) {
            setEditingPrime(prime);
            setForm({
                rubriqueId: prime.rubriqueId || "",
                modeCalcul: prime.modeCalcul || "FIXE",
                montantFixe: prime.montantFixe || "",
                pourcentage: prime.pourcentage || "",
                moisVersement: prime.moisVersement || "",
                actif: prime.actif !== false,
                dateDebut: prime.dateDebut || new Date().toISOString().slice(0, 10),
                dateFin: prime.dateFin || "",
                typesContrat: prime.typesContrat || [],
                tranches: prime.tranches || [],
                cibles: prime.cibles || []
            });
        } else {
            setEditingPrime(null);
            setForm(emptyPrime);
        }
        setSelectedEmploiId("");
        setSelectedJobId("");
        setActiveModalTab("identite");
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleContractTypeChange = (type) => {
        setForm(prev => {
            const index = prev.typesContrat.indexOf(type);
            let nextTypes = [...prev.typesContrat];
            if (index > -1) {
                nextTypes.splice(index, 1);
            } else {
                nextTypes.push(type);
            }
            return { ...prev, typesContrat: nextTypes };
        });
    };

    // Target Selection helpers (toggle list selections)
    const handleToggleTarget = (typeCible, targetId) => {
        setForm(prev => {
            let nextCibles = [...prev.cibles];
            
            // Check if already selected
            const index = nextCibles.findIndex(c => {
                if (c.typeCible !== typeCible) return false;
                if (typeCible === "EMPLOI" && c.emploiId === targetId) return true;
                if (typeCible === "POSTE" && c.jobId === targetId) return true;
                if (typeCible === "UTILISATEUR" && c.userId === targetId) return true;
                return false;
            });

            if (index > -1) {
                nextCibles.splice(index, 1);
            } else {
                const newCible = { typeCible };
                if (typeCible === "EMPLOI") {
                    const emp = emplois.find(e => e.id === targetId);
                    newCible.emploiId = targetId;
                    newCible.emploiNom = emp?.name || "";
                } else if (typeCible === "POSTE") {
                    const jb = jobs.find(j => j.id === targetId);
                    newCible.jobId = targetId;
                    newCible.jobNom = jb?.name || "";
                } else if (typeCible === "UTILISATEUR") {
                    const u = users.find(us => us.id === targetId);
                    newCible.userId = targetId;
                    newCible.userNom = u ? (u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim()) : "";
                }
                nextCibles.push(newCible);
            }
            return { ...prev, cibles: nextCibles };
        });
    };

    // Tranches manipulation
    const handleAddTranche = () => {
        setForm(prev => ({
            ...prev,
            tranches: [...prev.tranches, { minAnnees: 0, maxAnnees: "", taux: 0 }]
        }));
    };

    const handleRemoveTranche = (index) => {
        setForm(prev => {
            const nextTranches = [...prev.tranches];
            nextTranches.splice(index, 1);
            return { ...prev, tranches: nextTranches };
        });
    };

    const handleTrancheChange = (index, name, value) => {
        setForm(prev => {
            const nextTranches = [...prev.tranches];
            nextTranches[index] = {
                ...nextTranches[index],
                [name]: value === "" ? "" : Number(value)
            };
            return { ...prev, tranches: nextTranches };
        });
    };

    // Submit payload construction
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.rubriqueId) {
            Swal.fire({ icon: "warning", text: "Veuillez sélectionner une rubrique de paie." });
            return;
        }

        if (form.modeCalcul === "FIXE" && !form.montantFixe) {
            Swal.fire({ icon: "warning", text: "Le montant fixe est obligatoire." });
            return;
        }

        if (form.modeCalcul === "POURCENTAGE" && !form.pourcentage) {
            Swal.fire({ icon: "warning", text: "Le pourcentage est obligatoire." });
            return;
        }

        if (form.modeCalcul === "TRANCHE_ANCIENNETE" && form.tranches.length === 0) {
            Swal.fire({ icon: "warning", text: "Veuillez ajouter au moins une tranche d'ancienneté." });
            return;
        }

        // Formatting payloads
        const payload = {
            rubriqueId: Number(form.rubriqueId),
            modeCalcul: form.modeCalcul,
            montantFixe: form.modeCalcul === "FIXE" ? Number(form.montantFixe) : null,
            pourcentage: form.modeCalcul === "POURCENTAGE" ? Number(form.pourcentage) : null,
            moisVersement: form.moisVersement ? Number(form.moisVersement) : null,
            actif: form.actif,
            dateDebut: form.dateDebut,
            dateFin: form.dateFin || null,
            typesContrat: form.typesContrat,
            tranches: form.modeCalcul === "TRANCHE_ANCIENNETE" ? form.tranches.map(t => ({
                minAnnees: Number(t.minAnnees),
                maxAnnees: t.maxAnnees === "" || t.maxAnnees === null ? null : Number(t.maxAnnees),
                taux: Number(t.taux)
            })) : [],
            cibles: form.cibles.length === 0 ? [{ typeCible: "TOUS" }] : form.cibles.map(c => ({
                typeCible: c.typeCible,
                emploiId: c.emploiId || null,
                jobId: c.jobId || null,
                userId: c.userId || null
            }))
        };

        dispatch(payrollSaveResourceRequest(
            "parametragesPrimes",
            payload,
            editingPrime ? editingPrime.id : null
        ));
        setModalOpen(false);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Êtes-vous sûr ?",
            text: "Ce paramétrage de prime sera définitivement supprimé !",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b"
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(payrollDeleteResourceRequest("parametragesPrimes", id));
            }
        });
    };

    // Filter local primes display
    const filteredPrimes = primes.filter(p => {
        if (!searchTerm) return true;
        const codeMatch = p.rubriqueCode?.toLowerCase().includes(searchTerm.toLowerCase());
        const libelleMatch = p.rubriqueLibelle?.toLowerCase().includes(searchTerm.toLowerCase());
        return codeMatch || libelleMatch;
    });

    return (
        <div className="ppr-page">
            <div className="ppr-header">
                <div className="ppr-header-title">
                    <h1><Award size={28} color="#2f4595" /> Paramétrage des Primes</h1>
                    <p>Définissez les règles automatisées pour le versement des primes (Montant fixe, pourcentage ou grille d'ancienneté).</p>
                </div>
                <button className="ppr-btn ppr-btn-primary" onClick={() => handleOpenModal(null)}>
                    <Plus size={18} /> Configurer une prime
                </button>
            </div>

            <div className="ppr-card">
                <div className="ppr-toolbar">
                    <div className="ppr-search-wrapper">
                        <Search size={18} className="ppr-search-icon" />
                        <input
                            type="text"
                            placeholder="Rechercher par rubrique..."
                            className="ppr-search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="ppr-table-container">
                    <table className="ppr-table">
                        <thead>
                            <tr>
                                <th>Rubrique</th>
                                <th>Mode de calcul</th>
                                <th>Valeur / Grille</th>
                                <th>Mois versement</th>
                                <th>Populations ciblées</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPrimes.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: "center", color: "#64748b" }}>
                                        Aucun paramétrage de prime configuré
                                    </td>
                                </tr>
                            ) : (
                                filteredPrimes.map((p) => (
                                    <tr key={p.id}>
                                        <td style={{ fontWeight: 600 }}>
                                            <div>{p.rubriqueLibelle}</div>
                                            <span style={{ fontSize: 11, color: "#94a3b8" }}>{p.rubriqueCode}</span>
                                        </td>
                                        <td>
                                            <span className={`ppr-badge ppr-badge-${p.modeCalcul.toLowerCase()}`}>
                                                {p.modeCalcul === "FIXE" ? "Fixe" : p.modeCalcul === "POURCENTAGE" ? "Pourcentage" : "Tranche Ancienneté"}
                                            </span>
                                        </td>
                                        <td>
                                            {p.modeCalcul === "FIXE" && formatMoney(p.montantFixe)}
                                            {p.modeCalcul === "POURCENTAGE" && formatPercent(p.pourcentage)}
                                            {p.modeCalcul === "TRANCHE_ANCIENNETE" && `${p.tranches?.length || 0} tranches`}
                                        </td>
                                        <td>
                                            {p.moisVersement ? `Mois ${p.moisVersement}` : "Mensuel"}
                                        </td>
                                        <td>
                                            {p.cibles?.some(c => c.typeCible === "TOUS") ? (
                                                <span className="ppr-badge ppr-badge-cible">Tous les employés</span>
                                            ) : (
                                                p.cibles?.map(c => {
                                                    let val = "";
                                                    if (c.typeCible === "EMPLOI") val = `Emploi: ${c.emploiNom}`;
                                                    if (c.typeCible === "POSTE") val = `Poste: ${c.jobNom}`;
                                                    if (c.typeCible === "UTILISATEUR") val = `User: ${c.userNom}`;
                                                    return (
                                                        <span key={c.id} className="ppr-badge ppr-badge-cible">{val}</span>
                                                    );
                                                })
                                            )}
                                            {p.typesContrat?.map(tc => (
                                                <span key={tc} className="ppr-badge ppr-badge-cible" style={{ borderColor: "#2f4595", color: "#2f4595" }}>{tc}</span>
                                            ))}
                                        </td>
                                        <td>
                                            <span className={`ppr-badge ${p.actif ? "ppr-badge-active" : "ppr-badge-inactive"}`}>
                                                {p.actif ? "Actif" : "Inactif"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="ppr-actions-cell">
                                                <button className="ppr-icon-btn" onClick={() => handleOpenModal(p)}>
                                                    <Pencil size={14} />
                                                </button>
                                                <button className="ppr-icon-btn ppr-icon-btn-danger" onClick={() => handleDelete(p.id)}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {modalOpen && (
                <div className="ppr-overlay" onClick={handleCloseModal}>
                    <div className="ppr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ppr-modal-header">
                            <h3>{editingPrime ? "Modifier la configuration" : "Nouvelle configuration de prime"}</h3>
                            <button className="ppr-modal-close" onClick={handleCloseModal}>✕</button>
                        </div>

                        <div className="ppr-modal-tabs">
                            <button
                                className={`ppr-modal-tab ${activeModalTab === "identite" ? "active" : ""}`}
                                onClick={() => setActiveModalTab("identite")}
                            >
                                Identité & Valeurs
                            </button>
                            {form.modeCalcul === "TRANCHE_ANCIENNETE" && (
                                <button
                                    className={`ppr-modal-tab ${activeModalTab === "tranches" ? "active" : ""}`}
                                    onClick={() => setActiveModalTab("tranches")}
                                >
                                    Tranches Ancienneté
                                </button>
                            )}
                            <button
                                className={`ppr-modal-tab ${activeModalTab === "cibles" ? "active" : ""}`}
                                onClick={() => setActiveModalTab("cibles")}
                            >
                                Population Cible
                            </button>
                        </div>

                        <div className="ppr-modal-body">
                            {activeModalTab === "identite" && (
                                <div className="ppr-form">
                                    <div className="ppr-form-section-title">Paramètres Généraux</div>
                                    <div className="ppr-grid">
                                        <div className="ppr-field ppr-full-width">
                                            <label>Rubrique de Gain *</label>
                                            <select
                                                name="rubriqueId"
                                                value={form.rubriqueId}
                                                onChange={handleFormChange}
                                                disabled={!!editingPrime}
                                            >
                                                <option value="">-- Choisir une rubrique --</option>
                                                {rubriques.map(r => (
                                                    <option key={r.id} value={r.id}>{r.libelle} ({r.code})</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="ppr-field">
                                            <label>Mode de calcul *</label>
                                            <select
                                                name="modeCalcul"
                                                value={form.modeCalcul}
                                                onChange={handleFormChange}
                                            >
                                                <option value="FIXE">Montant fixe</option>
                                                <option value="POURCENTAGE">Pourcentage (du salaire de base)</option>
                                                <option value="TRANCHE_ANCIENNETE">Tranches d'ancienneté</option>
                                            </select>
                                        </div>

                                        {form.modeCalcul === "FIXE" && (
                                            <div className="ppr-field">
                                                <label>Montant Fixe (DH) *</label>
                                                <input
                                                    type="number"
                                                    name="montantFixe"
                                                    value={form.montantFixe}
                                                    onChange={handleFormChange}
                                                    placeholder="0.00"
                                                    step="0.01"
                                                />
                                            </div>
                                        )}

                                        {form.modeCalcul === "POURCENTAGE" && (
                                            <div className="ppr-field">
                                                <label>Pourcentage (%) *</label>
                                                <input
                                                    type="number"
                                                    name="pourcentage"
                                                    value={form.pourcentage}
                                                    onChange={handleFormChange}
                                                    placeholder="Ex: 5.00"
                                                    step="0.01"
                                                />
                                            </div>
                                        )}

                                        <div className="ppr-field">
                                            <label>Mois de versement (optionnel)</label>
                                            <select
                                                name="moisVersement"
                                                value={form.moisVersement}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Tous les mois (Mensuel)</option>
                                                <option value="1">Janvier</option>
                                                <option value="2">Février</option>
                                                <option value="3">Mars</option>
                                                <option value="4">Avril</option>
                                                <option value="5">Mai</option>
                                                <option value="6">Juin</option>
                                                <option value="7">Juillet</option>
                                                <option value="8">Août</option>
                                                <option value="9">Septembre</option>
                                                <option value="10">Octobre</option>
                                                <option value="11">Novembre</option>
                                                <option value="12">Décembre</option>
                                            </select>
                                        </div>

                                        <div className="ppr-field">
                                            <label>Statut</label>
                                            <label className="ppr-checkbox-group">
                                                <input
                                                    type="checkbox"
                                                    name="actif"
                                                    checked={form.actif}
                                                    onChange={handleFormChange}
                                                />
                                                <span>Actif (la prime sera calculée lors du bulletin)</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ppr-form-section-title">Validité Temporelle</div>
                                    <div className="ppr-grid">
                                        <div className="ppr-field">
                                            <label>Date de début *</label>
                                            <input
                                                type="date"
                                                name="dateDebut"
                                                value={form.dateDebut}
                                                onChange={handleFormChange}
                                            />
                                        </div>
                                        <div className="ppr-field">
                                            <label>Date de fin (optionnel)</label>
                                            <input
                                                type="date"
                                                name="dateFin"
                                                value={form.dateFin}
                                                onChange={handleFormChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeModalTab === "tranches" && form.modeCalcul === "TRANCHE_ANCIENNETE" && (
                                <div>
                                    <div className="ppr-form-section-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span>Grille d'ancienneté</span>
                                        <button type="button" className="ppr-btn ppr-btn-secondary" style={{ padding: "4px 8px", fontSize: 12 }} onClick={handleAddTranche}>
                                            <Plus size={14} /> Ajouter une tranche
                                        </button>
                                    </div>
                                    <table className="ppr-tranches-table">
                                        <thead>
                                            <tr>
                                                <th>Ancienneté Min (ans)</th>
                                                <th>Ancienneté Max (ans)</th>
                                                <th>Taux de prime (%)</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.tranches.length === 0 ? (
                                                <tr>
                                                    <td colSpan="4" style={{ textAlign: "center", color: "#64748b" }}>
                                                        Aucune tranche configurée. Veuillez en ajouter.
                                                    </td>
                                                </tr>
                                            ) : (
                                                form.tranches.map((t, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={t.minAnnees}
                                                                onChange={(e) => handleTrancheChange(idx, "minAnnees", e.target.value)}
                                                                style={{ width: "100%", padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                                min="0"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={t.maxAnnees}
                                                                onChange={(e) => handleTrancheChange(idx, "maxAnnees", e.target.value)}
                                                                placeholder="Illimité"
                                                                style={{ width: "100%", padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                                min="0"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={t.taux}
                                                                onChange={(e) => handleTrancheChange(idx, "taux", e.target.value)}
                                                                style={{ width: "100%", padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <button type="button" className="ppr-icon-btn ppr-icon-btn-danger" onClick={() => handleRemoveTranche(idx)}>
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {activeModalTab === "cibles" && (
                                <div>
                                    <div className="ppr-form-section-title">Targeting Type de Contrat (AND)</div>
                                    <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
                                        <label className="ppr-checkbox-group">
                                            <input
                                                type="checkbox"
                                                checked={form.typesContrat.includes("CDI")}
                                                onChange={() => handleContractTypeChange("CDI")}
                                            />
                                            <span>CDI</span>
                                        </label>
                                        <label className="ppr-checkbox-group">
                                            <input
                                                type="checkbox"
                                                checked={form.typesContrat.includes("CDD")}
                                                onChange={() => handleContractTypeChange("CDD")}
                                            />
                                            <span>CDD</span>
                                        </label>
                                        <span style={{ fontSize: 12, color: "#64748b" }}>(Si aucun coché, s'applique à tous les types)</span>
                                    </div>

                                    <div className="ppr-form-section-title">Targeting Structure & Collaborateurs (OR)</div>
                                    <p style={{ fontSize: 12, color: "#64748b", margin: "-10px 0 10px 0" }}>
                                        Cochez les emplois, postes ou personnes spécifiques. Si rien n'est sélectionné, la prime cible <strong>TOUS</strong> les employés.
                                    </p>

                                    <div className="ppr-cascade-container">
                                        {/* Emplois Column */}
                                        <div className="ppr-cascade-column">
                                            <div className="ppr-cascade-header">
                                                <span>Emploi</span>
                                                <Briefcase size={14} />
                                            </div>
                                            <div className="ppr-cascade-list">
                                                {filteredEmplois.map(e => {
                                                    const isChecked = form.cibles.some(c => c.typeCible === "EMPLOI" && c.emploiId === e.id);
                                                    return (
                                                        <div
                                                            key={e.id}
                                                            className={`ppr-cascade-item ${selectedEmploiId === String(e.id) ? "active" : ""}`}
                                                            onClick={() => setSelectedEmploiId(selectedEmploiId === String(e.id) ? "" : String(e.id))}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(ev) => {
                                                                    ev.stopPropagation();
                                                                    handleToggleTarget("EMPLOI", e.id);
                                                                }}
                                                            />
                                                            <span>{e.name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Jobs Column */}
                                        <div className="ppr-cascade-column">
                                            <div className="ppr-cascade-header">
                                                <span>Poste</span>
                                                <Shield size={14} />
                                            </div>
                                            <div className="ppr-cascade-list">
                                                {filteredJobs.map(j => {
                                                    const isChecked = form.cibles.some(c => c.typeCible === "POSTE" && c.jobId === j.id);
                                                    return (
                                                        <div
                                                            key={j.id}
                                                            className={`ppr-cascade-item ${selectedJobId === String(j.id) ? "active" : ""}`}
                                                            onClick={() => setSelectedJobId(selectedJobId === String(j.id) ? "" : String(j.id))}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(ev) => {
                                                                    ev.stopPropagation();
                                                                    handleToggleTarget("POSTE", j.id);
                                                                }}
                                                            />
                                                            <span>{j.name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Users Column */}
                                        <div className="ppr-cascade-column">
                                            <div className="ppr-cascade-header">
                                                <span>Utilisateur</span>
                                                <UsersRound size={14} />
                                            </div>
                                            <div className="ppr-cascade-list">
                                                {filteredUsers.map(u => {
                                                    const isChecked = form.cibles.some(c => c.typeCible === "UTILISATEUR" && c.userId === u.id);
                                                    const name = u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
                                                    return (
                                                        <div
                                                            key={u.id}
                                                            className="ppr-cascade-item"
                                                            onClick={() => handleToggleTarget("UTILISATEUR", u.id)}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => {}} // handled by click
                                                            />
                                                            <span>{name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="ppr-modal-footer">
                            <button className="ppr-btn ppr-btn-secondary" onClick={handleCloseModal}>
                                Annuler
                            </button>
                            <button className="ppr-btn ppr-btn-primary" onClick={handleSubmit}>
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParamPrimesPage;
